import React, { useState } from 'react';

/**
 * MediaPlaceholder — renders a real screenshot or video if the file exists at `src`.
 * Falls back to a styled placeholder with the suggested filename / aspect-ratio
 * if the file is missing (404 or load error). This lets the landing page render
 * cleanly during the period before all marketing assets are produced.
 *
 * Usage:
 *   <MediaPlaceholder
 *     type="image"
 *     src="/media/hero-product-screenshot.png"
 *     suggestedFilename="hero-product-screenshot.png"
 *     suggestedSize="1280×800"
 *     description="Crosstab view with banner + AI assistant panel"
 *     aspectRatio="16/10"
 *   />
 *
 *   <MediaPlaceholder
 *     type="video"
 *     src="/media/hero-demo.mp4"
 *     poster="/media/hero-poster.jpg"
 *     suggestedFilename="hero-demo.mp4"
 *     description="60-sec product walkthrough"
 *     aspectRatio="16/9"
 *   />
 */
const MediaPlaceholder = ({
  type = 'image',           // 'image' | 'video'
  src,                       // path to the real asset
  poster,                    // for video: poster image URL
  suggestedFilename = '',
  suggestedSize = '',
  description = '',
  aspectRatio = '16/9',
  rounded = true,
  shadow = true,
  alt = '',
  caption = '',              // optional small caption shown below the media
}) => {
  const [hasError, setHasError] = useState(false);

  const wrapStyle = {
    position: 'relative',
    width: '100%',
    aspectRatio,
    borderRadius: rounded ? '14px' : '0',
    overflow: 'hidden',
    boxShadow: shadow ? '0 12px 40px rgba(15, 23, 42, 0.12)' : 'none',
    background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
  };

  const isPlaceholder = hasError || !src;

  if (isPlaceholder) {
    return (
      <figure style={{ margin: 0 }}>
        <div style={{
          ...wrapStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          padding: '24px',
          border: '2px dashed #cbd5e1',
          background: 'repeating-linear-gradient(45deg, #f8fafc 0, #f8fafc 12px, #f1f5f9 12px, #f1f5f9 24px)',
        }}>
          {/* Icon */}
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%',
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: '16px',
            boxShadow: '0 4px 14px rgba(99, 102, 241, 0.25)',
          }}>
            {type === 'video' ? (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                <path d="M8 5v14l11-7z" />
              </svg>
            ) : (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5" fill="white"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
            )}
          </div>

          <div style={{ textAlign: 'center', maxWidth: '90%' }}>
            <p style={{
              fontWeight: 700, fontSize: '15px', color: '#1e293b', marginBottom: '6px',
            }}>
              {type === 'video' ? 'Video placeholder' : 'Image placeholder'}
            </p>
            {description && (
              <p style={{ fontSize: '13px', color: '#475569', marginBottom: '10px', lineHeight: 1.5 }}>
                {description}
              </p>
            )}
            {(suggestedFilename || suggestedSize) && (
              <div style={{
                display: 'inline-block',
                background: 'white', border: '1px solid #e2e8f0',
                borderRadius: '6px', padding: '6px 10px',
                fontFamily: '"JetBrains Mono", Consolas, Monaco, monospace',
                fontSize: '11px', color: '#64748b',
              }}>
                {suggestedFilename && <span><strong>file:</strong> {suggestedFilename}</span>}
                {suggestedFilename && suggestedSize && <span style={{ margin: '0 8px' }}>•</span>}
                {suggestedSize && <span><strong>size:</strong> {suggestedSize}</span>}
              </div>
            )}
          </div>
        </div>
        {caption && (
          <figcaption style={{
            fontSize: '12px', color: '#94a3b8', textAlign: 'center', marginTop: '8px', fontStyle: 'italic',
          }}>{caption}</figcaption>
        )}
      </figure>
    );
  }

  // Real asset rendering
  return (
    <figure style={{ margin: 0 }}>
      <div style={wrapStyle}>
        {type === 'video' ? (
          <video
            src={src}
            poster={poster}
            controls
            playsInline
            preload="metadata"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={() => setHasError(true)}
          />
        ) : (
          <img
            src={src}
            alt={alt || description}
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={() => setHasError(true)}
          />
        )}
      </div>
      {caption && (
        <figcaption style={{
          fontSize: '12px', color: '#94a3b8', textAlign: 'center', marginTop: '8px', fontStyle: 'italic',
        }}>{caption}</figcaption>
      )}
    </figure>
  );
};

export default MediaPlaceholder;
